<template>
  <div
    :class="[
      $style.dateSelectInput,
      disabled ? $style.disabled : '',
      error && $style.error,
    ]"
  >
    <div :class="$style.input">
      <span
        v-once
        :class="$style.icon"
      >
        <IconCalendar />
      </span>

      <Datepicker
        :id="id"
        :language="locale"
        :typeable="false"
        :value="value"
        :placeholder="placeholder"
        :input-class="$style.datePickerInput"
        :calendar-class="$style.datePickerCalendar"
        :minimum-view="minimumView"
        :maximum-view="maximumView"
        :open-date="new Date(openDate)"
        initial-view="year"
        :format="format"
        :disabled="disabled"
        @selected="handleSelect"
      />
    </div>
    <transition name="slide-fade">
      <Error v-if="error">
        {{ error }}
      </Error>
    </transition>
  </div>
</template>

<style lang="scss" module>
.dateSelectInput {
  position: relative;

  &.disabled {
    opacity: 0.3;
  }

  &::after {
    content: "";
    display: block;
    clear: both;
  }
}

.input {
  position: relative;
}

.icon {
  position: absolute;
  right: 16px;
  top: 50%;
  margin-top: -8px;
  width: 16px;
  height: 16px;

  svg {
    width: 100%;
    height: 100%;

    path {
      fill: #a0a0a0;
      transition: 0.3s fill;

      .dateSelectInput:hover & {
        fill: #6c6c6c;
      }
    }
  }
}

.datePickerCalendar {
  border: solid 1px #dfdfdf;
  border-radius: 5px;
  background-color: #fff;
  box-shadow: 0 7px 10px 0 rgba(109, 109, 109, 0.07);
  width: 100%;
  margin-top: 4px;

  :global .cell {
    border-radius: 5px;
    font-size: 1.4rem;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    cursor: pointer;
    letter-spacing: 0.7px;
    color: #4f4f4f;

    &:hover {
      background-color: #f5f5f5;
      border-color: transparent !important;
    }

    &.selected,
    &.selected:hover {
      background-color: #f5f5f5;
    }
  }
}

.datePickerInput {
  font-size: 1.4rem;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  width: 100%;
  padding: 10px 16px;
  transition: 0.3s border-color;
  letter-spacing: 0.7px;
  color: #3c3c3c;
  border: solid 1px #cfcfcf;
  border-radius: 5px;
  outline: none;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    border-color: #acacac;
  }

  &:focus {
    border-color: #eea2b1;
  }

  &::placeholder {
    font-size: 1.4rem;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: 0.6px;
    opacity: 0.23;
    color: $color-text;
  }

  .error & {
    border-color: $red;
    caret-color: $red;

    &:focus {
      border-color: $red;
      caret-color: $red;
    }
  }
}
</style>

<script>
import Datepicker from 'vuejs-datepicker';
import Error from '../others/Error';
import IconCalendar from '@/images/icons/calendar.svg';
import { zh } from 'vuejs-datepicker/dist/locale';
import moment from 'moment';

export default {
  components: {
    Error,
    Datepicker,
    IconCalendar,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: 'full',
      validator(value) {
        return ['full', 'year', 'monthAndYear'].indexOf(value) !== -1;
      },
    },
    value: {
      type: [Date, String],
      default: null,
    },
    error: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    openDate: {
      type: String,
      default: '2010',
    },
  },

  data() {
    return {
      locale: zh,
    };
  },

  computed: {
    minimumView() {
      switch (this.type) {
        case 'year':
          return 'year';

        case 'monthAndYear':
          return 'month';

        case 'full':
          return 'day';
      }
      return '';
    },
    maximumView() {
      switch (this.type) {
        case 'year':
          return 'year';

        case 'monthAndYear':
          return 'year';

        case 'full':
          return 'year';
      }
      return '';
    },
    format() {
      switch (this.type) {
        case 'year':
          return 'yyyy';

        case 'monthAndYear':
          return 'yyyy/MM';

        case 'full':
          return 'yyyy/MM/dd';
      }
      return 'yyyy/MM/dd';
    },

    placeholder() {
      switch (this.type) {
        case 'year':
          return 'YYYY';

        case 'monthAndYear':
          return 'YYYY/MM';

        case 'full':
          return 'YYYY/MM/DD';
      }
      return 'YYYY/MM/DD';
    },
  },
  methods: {
    handleSelect(date) {
      this.$emit('update:value', moment(date).format('YYYY/MM/DD'));
    },
  },
};
</script>
